import { useMemo, useState } from 'react';
import {
  Table,
  useQueryState,
  IconDownload,
  IconLoader,
  formatDate,
} from '@ui/src';
import {
  agreementsListColumns,
  agreementsListSchema,
} from './variables/agreements';
import { ApplicationAgreement } from '../../../hoc/new-application/types';
import { useLazyGetCustomersAgreementFileQuery } from '../../../hoc/new-application/store/services';
import getAgreementTypeName from '../../../hoc/new-application/helpers/get-agreement-type-name';
import { IndividualApplicationStepFourTypes } from '../types';
import StepPartBlock from '../corporate/parts/step-part-block';

import cn from '../new-application.module.css';

const formatAgreementsData = (
  list: ApplicationAgreement[],
  getAgreementFile: (data: string) => void,
  downloadFile?: string,
  fileDownloadInProgress?: boolean,
): ApplicationAgreement[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {
      type: <div>{getAgreementTypeName(result.type)}</div>,
      startAt: (
        <div className={cn.dateOfSigning}>
          <div>
            {result.signatures?.edges[0]?.node?.signedAt
              ? formatDate(result.signatures.edges[0].node.signedAt)
              : '-'}
          </div>
          <button
            type="button"
            aria-label="Download agreement button"
            className={cn.downloadButton}
            onClick={() => getAgreementFile(result.id)}
            disabled={fileDownloadInProgress && result.id === downloadFile}
          >
            {fileDownloadInProgress && result.id === downloadFile ? (
              <IconLoader width={16} height={16} />
            ) : (
              <IconDownload />
            )}
          </button>
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const NewApplicationInfoStepFour = ({
  list,
  errors,
}: IndividualApplicationStepFourTypes) => {
  const [downloadFile, setDownloadedFile] = useState('');

  const { filters, sort, onSortChange, onFilterChange } =
    useQueryState(agreementsListSchema);

  const [
    getFile,
    { isLoading: fileDownloadInProgress, isFetching: fileDownloadIsFetching },
  ] = useLazyGetCustomersAgreementFileQuery();

  const getAgreementFile = async (id: string) => {
    setDownloadedFile(id);

    const response = await getFile(id);

    if (response.data) {
      const currentAgreement = list?.find((item) => item.id === id);

      if (currentAgreement) {
        const hiddenElement = document.createElement('a');
        const url = window.URL || window.webkitURL;

        // @ts-ignore
        const blob = url.createObjectURL(response.data);
        hiddenElement.href = blob;
        hiddenElement.target = '_blank';
        hiddenElement.download = `${getAgreementTypeName(
          currentAgreement?.type,
        )}(v${currentAgreement?.version})`;
        hiddenElement.click();

        setDownloadedFile('');
      }
    }
  };

  const formattedData = useMemo(() => {
    const data = formatAgreementsData(
      list as ApplicationAgreement[],
      getAgreementFile,
      downloadFile,
      fileDownloadInProgress || fileDownloadIsFetching,
    );

    return data;
  }, [
    list,
    downloadFile,
    fileDownloadInProgress,
    fileDownloadIsFetching,
    getAgreementFile,
  ]);

  return (
    <div className={cn.stepBlockWrapper} style={{ zIndex: 17 }}>
      <div className={cn.stepBlockContent}>
        <StepPartBlock title="Agreements" zIndex={9}>
          <div className={cn.agreementsTableWrapper} id="agreementIds">
            {errors?.agreements?.message && (
              <div className={cn.sumsubError}>
                {errors?.agreements?.message}
              </div>
            )}
            <Table
              columns={agreementsListColumns}
              data={formattedData || []}
              columnsLabels={{}}
              sort={sort}
              filters={filters}
              onSort={onSortChange}
              onFilterChange={onFilterChange}
              clickable
            />
          </div>
        </StepPartBlock>
      </div>
    </div>
  );
};

export default NewApplicationInfoStepFour;
